import { call, put, takeLatest } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { axiosRestInstance } from "../../../common/axios";
import {
  featureManagementSagaActionTypes,
  getFeaturesSagaAction,
} from "./saga-actions";
import { featureManagementSliceAction } from "../../slices/feature-management";
import {
  CreateFeaturePayloadType,
  CreateFeatureSagaPayloadType,
  DeleteFeatureSagaPayloadType,
  FeatureTechnologyOption,
  GetFeaturesApiResponse,
  UpdateFeatureSagaPayloadType,
} from "../../slices/feature-management/Types";
import { GetTechnologiesApiResponse } from "../../slices/technology-settings/technologies/Types";

const getFeaturesAPI = async () => {
  const response = await axiosRestInstance.get<
    AxiosResponse<GetFeaturesApiResponse>
  >("/project/feature/getFeatures/");
  return response.data;
};

const getFeatureTechnologyOptionsAPI = async () => {
  const response = await axiosRestInstance.get<
    AxiosResponse<GetTechnologiesApiResponse>
  >("/technology-settings/technology/getData");
  return response.data;
};

type createFeatureAPIResponseType = {
  success: boolean;
  message?: string;
};

const createFeatureAPI = async (payload: CreateFeaturePayloadType) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<createFeatureAPIResponseType>
  >("/project/feature/createFeature/", payload);
  return response.data;
};

const deleteFeatureAPI = async (id: string | number) => {
  const response = await axiosRestInstance.delete<
    AxiosResponse<createFeatureAPIResponseType>
  >(`/project/feature/deleteFeature/${id}`);
  return response.data;
};

const updateFeatureAPI = async (
  id: string | number,
  payload: CreateFeaturePayloadType
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<createFeatureAPIResponseType>
  >(`/project/feature/updateFeature/${id}`, payload);
  return response.data;
};

function* getFeaturesSaga() {
  try {
    yield put(featureManagementSliceAction.setFeaturesLoading(true));
    const response: GetFeaturesApiResponse = yield call(getFeaturesAPI);
    yield put(featureManagementSliceAction.setFeatures(response.data || []));
  } catch (e) {
    yield put(featureManagementSliceAction.setFeatures([]));
    console.log(e);
  } finally {
    yield put(featureManagementSliceAction.setFeaturesLoading(false));
  }
}

function* getFeatureTechnologyOptionsSaga() {
  try {
    yield put(featureManagementSliceAction.setTechnologyOptionsLoading(true));
    const response: GetTechnologiesApiResponse = yield call(
      getFeatureTechnologyOptionsAPI
    );
    const technologies = response.data || [];
    const toOptions = (items: typeof technologies) =>
      items
        .map<FeatureTechnologyOption>((item) => ({
          id: item.id,
          name: item.technology_name,
        }))
        .filter((item) => item.name);

    yield put(
      featureManagementSliceAction.setTechnologyOptions(
        toOptions(technologies.filter((item) => item.status === 1))
      )
    );
    yield put(
      featureManagementSliceAction.setInactiveTechnologyOptions(
        toOptions(technologies.filter((item) => item.status !== 1))
      )
    );
  } catch (e) {
    yield put(featureManagementSliceAction.setTechnologyOptions([]));
    yield put(featureManagementSliceAction.setInactiveTechnologyOptions([]));
    console.log(e);
  } finally {
    yield put(featureManagementSliceAction.setTechnologyOptionsLoading(false));
  }
}

function* createFeatureSaga(action: {
  type: string;
  payload: CreateFeatureSagaPayloadType;
}) {
  try {
    yield put(featureManagementSliceAction.setCreateFeatureLoading(true));
    const response: createFeatureAPIResponseType = yield call(
      createFeatureAPI,
      action.payload.data
    );
    if (response.success) {
      yield put(getFeaturesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(response.message || "Feature created successfully.");
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to create feature.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(e?.response?.data?.message || "Unable to create feature.");
    }
    console.log(e);
  } finally {
    yield put(featureManagementSliceAction.setCreateFeatureLoading(false));
  }
}

function* updateFeatureSaga(action: {
  type: string;
  payload: UpdateFeatureSagaPayloadType;
}) {
  try {
    yield put(featureManagementSliceAction.setUpdateFeatureLoading(true));
    const response: createFeatureAPIResponseType = yield call(
      updateFeatureAPI,
      action.payload.id,
      action.payload.data
    );
    if (response.success) {
      yield put(getFeaturesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(response.message || "Feature updated successfully.");
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to update feature.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(e?.response?.data?.message || "Unable to update feature.");
    }
    console.log(e);
  } finally {
    yield put(featureManagementSliceAction.setUpdateFeatureLoading(false));
  }
}

function* deleteFeatureSaga(action: {
  type: string;
  payload: DeleteFeatureSagaPayloadType;
}) {
  try {
    yield put(featureManagementSliceAction.setDeleteFeatureLoading(true));
    const response: createFeatureAPIResponseType = yield call(
      deleteFeatureAPI,
      action.payload.id
    );
    if (response.success) {
      yield put(getFeaturesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(response.message || "Feature deleted successfully.");
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to delete feature.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(e?.response?.data?.message || "Unable to delete feature.");
    }
    console.log(e);
  } finally {
    yield put(featureManagementSliceAction.setDeleteFeatureLoading(false));
  }
}

function* featureManagementSaga() {
  yield takeLatest(
    featureManagementSagaActionTypes.callGetFeaturesSaga,
    getFeaturesSaga
  );
  yield takeLatest(
    featureManagementSagaActionTypes.callGetFeatureTechnologyOptionsSaga,
    getFeatureTechnologyOptionsSaga
  );
  yield takeLatest(
    featureManagementSagaActionTypes.callCreateFeatureSaga,
    createFeatureSaga
  );
  yield takeLatest(
    featureManagementSagaActionTypes.callUpdateFeatureSaga,
    updateFeatureSaga
  );
  yield takeLatest(
    featureManagementSagaActionTypes.callDeleteFeatureSaga,
    deleteFeatureSaga
  );
}

export default featureManagementSaga;
